import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, CheckCircle2, Compass, Sparkles } from 'lucide-react';
import { guideSteps } from './guideSteps';

function OverlayShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden animate-slide-up max-h-[92vh] overflow-y-auto">
        {children}
      </div>
    </div>
  );
}

interface WelcomeOverlayProps {
  badge: string;
  title: string;
  text: React.ReactNode;
  skipLabel: string;
  onStart: () => void;
  onSkip: () => void;
}

export function WelcomeOverlay({ badge, title, text, skipLabel, onStart, onSkip }: WelcomeOverlayProps) {
  return (
    <OverlayShell>
      <div className="bg-gradient-to-br from-teal-800 to-teal-950 px-8 pt-8 pb-7 text-white relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/5" />
        <div className="absolute right-12 bottom-0 w-20 h-20 rounded-full bg-amber-400/10" />
        <div className="inline-flex items-center gap-1.5 bg-white/10 text-amber-200 text-xs font-medium px-2.5 py-1 rounded-full mb-4">
          <Sparkles className="w-3.5 h-3.5" /> {badge}
        </div>
        <h1 className="text-2xl font-bold leading-tight">{title}</h1>
        <p className="text-teal-100 mt-2 text-sm leading-relaxed">{text}</p>
      </div>

      <div className="px-8 py-6">
        <p className="text-sm font-medium text-gray-900 mb-3">Guiden tar dig igenom {guideSteps.length} steg</p>
        <ol className="space-y-2">
          {guideSteps.map((step, i) => (
            <li key={step.id} className="flex items-center gap-3 text-sm text-gray-600">
              <span className="w-7 h-7 rounded-lg bg-teal-50 text-teal-700 flex items-center justify-center flex-shrink-0">
                <step.icon className="w-4 h-4" />
              </span>
              <span><span className="text-gray-400 mr-1">{i + 1}.</span>{step.title}</span>
            </li>
          ))}
        </ol>

        <div className="flex flex-col-reverse sm:flex-row gap-3 mt-7">
          <button onClick={onSkip} className="px-5 py-2.5 rounded-xl text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors">
            {skipLabel}
          </button>
          <button
            onClick={onStart}
            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-teal-700 hover:bg-teal-800 text-white text-sm font-medium transition-colors group"
          >
            Starta guiden
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </button>
        </div>
      </div>
    </OverlayShell>
  );
}

interface FinishOverlayProps {
  title: string;
  tasksDone: number;
  tasksTotal: number;
  visitorLoggedIn: boolean;
  exitPath: string;
  onContinue: () => void;
}

export function FinishOverlay({ title, tasksDone, tasksTotal, visitorLoggedIn, exitPath, onContinue }: FinishOverlayProps) {
  return (
    <OverlayShell>
      <div className="px-8 pt-10 pb-8 text-center">
        <div className="relative inline-flex mb-5">
          <div className="absolute inset-0 rounded-full bg-emerald-400/30 animate-ping" />
          <div className="relative w-16 h-16 rounded-full bg-emerald-500 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-white" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 leading-tight">{title}</h1>
        <p className="text-gray-500 mt-2 text-sm leading-relaxed max-w-sm mx-auto">
          Från föreningsuppgifter och medlemmar till möten, betalningar, ekonomi och dokument. Allt samlat på ett ställe.
        </p>

        <div className="mt-6 grid grid-cols-3 gap-3">
          <div className="rounded-2xl bg-gray-50 py-3">
            <p className="text-xl font-bold text-gray-900">{guideSteps.length}</p>
            <p className="text-xs text-gray-500">steg</p>
          </div>
          <div className="rounded-2xl bg-gray-50 py-3">
            <p className="text-xl font-bold text-gray-900">{tasksDone}/{tasksTotal}</p>
            <p className="text-xs text-gray-500">uppgifter klara</p>
          </div>
          <div className="rounded-2xl bg-gray-50 py-3">
            <p className="text-xl font-bold text-gray-900">0 kr</p>
            <p className="text-xs text-gray-500">att komma igång</p>
          </div>
        </div>

        {visitorLoggedIn ? (
          <Link
            to="/organizations"
            className="mt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-teal-700 hover:bg-teal-800 text-white font-semibold transition-all"
          >
            Tillbaka till mina föreningar
            <ArrowRight className="w-4 h-4" />
          </Link>
        ) : (
          <Link
            to="/register"
            className="mt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-teal-700 hover:bg-teal-800 text-white font-semibold transition-all shadow-lg shadow-teal-700/20 hover:shadow-teal-700/30 hover:-translate-y-0.5 group"
          >
            <Building2 className="w-5 h-5" />
            Skapa konto och starta provperiod
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        )}

        <div className="flex items-center justify-center gap-4 mt-4 text-sm">
          <button onClick={onContinue} className="inline-flex items-center gap-1.5 text-gray-500 hover:text-gray-900 transition-colors">
            <Compass className="w-4 h-4" /> Fortsätt utforska testföreningen
          </button>
          {!visitorLoggedIn && (
            <>
              <span className="text-gray-300">|</span>
              <Link to={exitPath} className="text-gray-500 hover:text-gray-900 transition-colors">Lämna testföreningen</Link>
            </>
          )}
        </div>
      </div>
    </OverlayShell>
  );
}
